/**
 * useClientData mirrors the previous client-fetch logic from App.tsx.
 * It retrieves client profile data for logged-in CLIENT roles, handles
 * loading/error states, and exposes the selected client plus a refetch helper.
 */
import { fetchAuthSession } from 'aws-amplify/auth';
import axios from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { Client } from '../../types/clientTypes';
import { toGenderDisplay } from '../../utils/gender';
import { applyTestDelay } from '../../utils/testLoadingDelay';

type NetworkError = {
  message: string;
  isAuthError?: boolean;
};

type UseClientDataParams = {
  username: string | null;
  userRole: string | null;
};

export const useClientData = ({ username, userRole }: UseClientDataParams) => {
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [isClientLoading, setIsClientLoading] = useState(true);
  const [networkError, setNetworkError] = useState<NetworkError | null>(null);

  const fetchClientData = useCallback(async () => {
    if (username && userRole === 'CLIENT') {
      setIsClientLoading(true);
      setNetworkError(null);
      try {
        await applyTestDelay('clientDataDelay');
        const session = await fetchAuthSession();
        if (session?.tokens?.accessToken) {
          localStorage.setItem('session', JSON.stringify(session));
          const response = await axios.get(`users/users/${username}`, {
            headers: {
              Authorization: `Bearer ${session.tokens.accessToken}`,
            },
          });
          const profile = response.data?.Profile;
          if (!profile) {
            console.error('Profile data is missing');
            setIsClientLoading(false);
            return;
          }
          const clientData: Client = {
            id: profile.user_profile_id ?? username,
            client_id: profile.client_id,
            name:
              profile.first_name && profile.last_name
                ? `${profile.first_name} ${profile.last_name}`
                : 'Unknown',
            first_name: profile.first_name || '',
            last_name: profile.last_name || '',
            title: profile.title || '',
            gender: toGenderDisplay(profile.gender),
            age: profile.date_of_birth
              ? Math.floor(
                  (new Date().getTime() - new Date(profile.date_of_birth).getTime()) / 31557600000
                )
              : 0,
            date_of_birth: profile.date_of_birth || '',
            phone_number: profile.phone_number || 'Not specified',
            alternate_phone: profile.alternate_phone,
            alternate_email: profile.alternate_email,
            preferred_contact_method: profile.preferred_contact_method || 'phone',
            city: profile.city || 'Not specified',
            state: profile.state,
            country: profile.country,
            zipcode: profile.zipcode,
            address_line_1: profile.address_line_1,
            address_line_2: profile.address_line_2,
            image: '/avatars/default.png',
            profile_picture: profile.profile_picture,
            email: profile.email,
            role: profile.role || 'client',
            occupation: profile.occupation || 'Not specified',
            relationship_status: profile.relationship_status || 'Not specified',
            relationship_status_custom: profile.relationship_status_custom || null,
            organization: profile.organization,
            clinic: profile.clinic,
            emergency_contact_name: profile.emergency_contact_name,
            emergency_contact_relation: profile.emergency_contact_relation,
            emergency_contact_number: profile.emergency_contact_number,
            appointment_duration: profile.appointment_duration || '00:30:00',
            health_goals: profile.health_goals || {},
            client_story: profile.client_story || {},
            lifestyle_history: profile.lifestyle_history || {},
            family_history: profile.family_history || {},
            abha_number: profile.abha_number || null,
            aadhar_number: profile.aadhar_number || null,
            hipaa_consent: profile.hipaa_consent || false,
            hipaa_consent_date: profile.hipaa_consent_date || null,
            hipaa_consent_version: profile.hipaa_consent_version || null,
            program_names: profile.program_names || [],
            addon_names: profile.addon_names || [],
            membership_names: profile.membership_names || [],
          };
          setSelectedClient(clientData);
        }
      } catch (error: any) {
        console.error('Error fetching client data:', error);
        const status = error.response?.status;
        const errorDetail =
          error.response?.data?.detail ||
          error.response?.data?.error ||
          error.response?.data?.message ||
          error.message ||
          '';
        const errorDetailLower = errorDetail.toLowerCase();
        const isTokenExpired =
          errorDetailLower.includes('expired') ||
          errorDetailLower.includes('token has expired') ||
          errorDetailLower.includes('token expired') ||
          errorDetailLower.includes('invalid token') ||
          errorDetailLower.includes('token is invalid') ||
          errorDetailLower.includes('not authorized');

        if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
          setNetworkError({
            message: 'Unable to connect to the server. Please check if the backend is running.',
            isAuthError: false,
          });
        } else if (status === 401 || isTokenExpired) {
          setNetworkError({
            message: 'Your session has expired. Please sign in again.',
            isAuthError: true,
          });
        } else if (status === 403) {
          setNetworkError({
            message: 'You do not have permission to access this resource.',
            isAuthError: false,
          });
        } else if (status && status >= 500) {
          setNetworkError({
            message: 'The server encountered an error. Please try again later.',
            isAuthError: false,
          });
        } else {
          setNetworkError({
            message: errorDetail || 'An error occurred while fetching your data.',
            isAuthError: false,
          });
        }
      } finally {
        setIsClientLoading(false);
      }
    } else if (userRole !== 'CLIENT') {
      setIsClientLoading(false);
    }
  }, [username, userRole]);

  useEffect(() => {
    fetchClientData();
  }, [fetchClientData]);

  return {
    selectedClient,
    isClientLoading,
    networkError,
    fetchClientData,
    setSelectedClient,
  };
};
